import {
  Button,
  Icon,
  PhoneMockup,
  PhoneMockupBodyLayout,
  PhoneMockupLayout,
  TextLink,
  WidgetHeader,
  WidgetHeaderContent,
  WidgetStatus,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { useEffect } from 'react';

import { PageFields_PaymentRejectedPage_Fragment as PaymentRejectedPageField } from '../../graphql/generated';

import {
  TopBar,
  TransactionDropdown,
  addStatusToTransactions,
} from '../../components';

import { ReactComponent as SquareArrowTopRightBold } from '@frontend/icons/square-arrow-top-right-bold.svg';
import links from '../../constants/links';
import { useWidgetContext } from '../../context/WidgetContext';
import { useRestartBill } from '../../hooks';

interface PaymentRejectedProps {
  data: PaymentRejectedPageField['pageData'];
}

const PaymentRejected = ({ data }: PaymentRejectedProps) => {
  const { configureHeader } = useWidgetContext();

  useEffect(() => {
    configureHeader({
      id: getIdFromUrl(),
      expiresAt: data?.paymentRejectedExpiresAt,
      merchantName: data?.merchantName || '',
      billType: data?.billType,
    });
  }, [data?.paymentRejectedExpiresAt]);

  const { restartBill, loading: restartBillLoading } = useRestartBill();

  const id = getIdFromUrl();

  const handleRestartPaymentClick = () => {
    restartBill({
      variables: {
        id,
        type: 'bill',
        reason: 'kyt_rejected',
      },
    });
  };

  return (
    <PhoneMockupLayout>
      <PhoneMockup
        header={
          <WidgetHeader color="error">
            <WidgetHeaderContent title="Transaction Rejected" />
          </WidgetHeader>
        }
      >
        <PhoneMockupBodyLayout
          header={
            <TopBar
              rightSlot={
                data?.paymentRejectedConfirmedTransactions && (
                  <TransactionDropdown
                    transactions={addStatusToTransactions(
                      data.paymentRejectedConfirmedTransactions,
                      'confirmed'
                    )}
                  />
                )
              }
            />
          }
          bodyCentered
          body={
            <WidgetStatus
              emoji="😕"
              title="Transaction Rejected"
              description={
                <>
                  Your latest transaction could not be processed
                  <br />
                  You will receive an email for any transactions
                  <br />
                  that meet{' '}
                  <TextLink
                    size="inherit"
                    href={links.support.REFUND_POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    our refund policy.
                  </TextLink>
                </>
              }
            />
          }
          footer={
            <>
              <Button
                as="a"
                size="large"
                href={links.support.PAYMENT_CONFIRMATION}
                target="_blank"
                rel="noopener noreferrer"
                variant="ghost"
                rightIcon={<Icon component={<SquareArrowTopRightBold />} />}
              >
                Learn more
              </Button>

              {data?.returnUrl && (
                <Button
                  as="a"
                  href={data.returnUrl}
                  mt="16"
                  size="large"
                  variant="secondary"
                >
                  Go back to store
                </Button>
              )}

              <Button
                variant="primary"
                size="large"
                mt="16"
                onClick={handleRestartPaymentClick}
                loading={restartBillLoading}
              >
                Try again
              </Button>
            </>
          }
        />
      </PhoneMockup>
    </PhoneMockupLayout>
  );
};

export default PaymentRejected;
